import { Injectable } from '@angular/core';

/**
 * libs
 */
import * as XLSX from 'xlsx';

/**
 * interfaces
 */
import { ExportFormat } from '../types';
import { IExcelExporter, IExcelExportRequest } from '../interfaces';

/**
 * utils
 */
import { ensureFileExtension, normalizeExportValue, resolveExportCellValue } from './export-row.utils';

/**
 * Excel (.xlsx) export strategy implementation.
 */
@Injectable()
export class ExcelExporterService implements IExcelExporter {

  /**
   * Sheet name used when the request does not supply one.
   */
  private static readonly _DEFAULT_SHEET_NAME = 'Sheet1';

  /**
   * The format of the exporter.
   */
  public readonly format: ExportFormat = 'xlsx';

  /**
   * Export the data to an Excel file.
   *
   * @param request - The export request. Must carry `rows` and `columns`.
   * @throws When the request carries no `rows` / `columns` pair.
   *
   * @remarks
   * One table on one sheet per file, named by `metadata.sheetName` or
   * `Sheet1`. `rows` and `columns` are optional on
   * {@link IExportRequest} only because other formats — an image, and a
   * sectioned PDF — do not use them; Excel always needs them, so their absence is
   * rejected here with a clear message rather than failing later as an unclear
   * crash.
   *
   * Cell values keep the type {@link normalizeExportValue} gives them, so a
   * number reaches `aoa_to_sheet` as a number and Excel writes a numeric cell.
   * Routing them through the PDF renderer's string-only cell helper would make
   * every column text, losing sorting, `SUM` and number formatting.
   *
   * `async` only to satisfy {@link IExporter}, whose `export` is
   * `Promise<void>` for every implementer. Nothing here is asynchronous.
   */
  public async export<TRow = unknown>(request: IExcelExportRequest<TRow>): Promise<void> {
    const { rows, columns } = request;

    if (rows == null || columns == null) {
      throw new Error('Excel export only supports a single table (rows + columns); multi-section export is not supported.');
    }

    const fileName = ensureFileExtension(request.fileName, '.xlsx');
    const header = columns.map((column) => column.header);

    const body = rows.map((row) => {
      return columns.map((column) => {
        const value = resolveExportCellValue(row, column);
        return normalizeExportValue(value);
      });
    });

    const worksheet = XLSX.utils.aoa_to_sheet([header, ...body]);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      request.metadata?.sheetName ?? ExcelExporterService._DEFAULT_SHEET_NAME,
    );
    XLSX.writeFile(workbook, fileName, { compression: true });
  }

}
